import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ShieldCheck, Truck, RefreshCcw, DollarSign } from "lucide-react";

const faqs = [
  {
    question: "How long does shipping take?",
    answer: "Orders are processed within 24–48 hours. Standard delivery across India takes 5–8 business days after dispatch.",
  },
  {
    question: "Can I return or exchange my T-shirt?",
    answer: "Return or exchange requests are accepted within 7 days of delivery when the product is unused, unwashed, and in original packaging.",
  },
  {
    question: "When will I receive a refund?",
    answer: "Refunds are processed after a quality check. Expect the refund within 5–7 working days after approval.",
  },
];

export default function ShippingReturns() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    document.title = "Shipping & Returns | Cynos";
  }, []);

  return (
    <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-6xl space-y-12">
        <div className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
          <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Shipping & Returns</p>
          <h1 className="mt-4 text-5xl font-anton uppercase text-white">Trust the process.</h1>
          <p className="mt-4 max-w-2xl text-sm text-zinc-400">Clear policies and quick support for every order. Shop with confidence in premium Indian streetwear.</p>
        </div>

        <div className="grid gap-8 xl:grid-cols-3">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-white/10 bg-black/40 p-6">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-cynos-red/10 text-cynos-red">
              <Truck size={20} />
            </div>
            <h2 className="mt-6 text-xl font-semibold text-white">Shipping Policy</h2>
            <ul className="mt-4 space-y-3 text-sm text-zinc-300">
              <li>Orders are processed within 24–48 hours.</li>
              <li>Standard delivery timeline: 5–8 business days across India.</li>
              <li>Tracking details shared after dispatch.</li>
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-white/10 bg-black/40 p-6">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-cynos-red/10 text-cynos-red">
              <RefreshCcw size={20} />
            </div>
            <h2 className="mt-6 text-xl font-semibold text-white">Returns & Exchange</h2>
            <ul className="mt-4 space-y-3 text-sm text-zinc-300">
              <li>Requests allowed within 7 days of delivery.</li>
              <li>Product must be unused, unwashed, and in original packaging.</li>
              <li>Size exchange subject to stock availability.</li>
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-white/10 bg-black/40 p-6">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-cynos-red/10 text-cynos-red">
              <DollarSign size={20} />
            </div>
            <h2 className="mt-6 text-xl font-semibold text-white">Refund Policy</h2>
            <ul className="mt-4 space-y-3 text-sm text-zinc-300">
              <li>Refund processed after product quality check.</li>
              <li>Refund timeline: 5–7 working days after approval.</li>
              <li>Final sale items may not be eligible for refund.</li>
            </ul>
          </motion.div>
        </div>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
          <h2 className="text-3xl font-semibold text-white">Non-returnable cases</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {[
              "Used or washed products",
              "Damaged by customer",
              "Missing tags or original packaging",
              "Final sale or clearance items",
            ].map((item) => (
              <div key={item} className="rounded-3xl border border-white/10 bg-black/40 p-4 text-sm text-zinc-300">{item}</div>
            ))}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
          <h2 className="text-3xl font-semibold text-white">Frequently asked questions</h2>
          <div className="mt-6 space-y-4">
            {faqs.map((item, index) => (
              <button
                key={item.question}
                type="button"
                onClick={() => setActiveIndex(index === activeIndex ? -1 : index)}
                className="w-full rounded-3xl border border-white/10 bg-black/40 p-5 text-left"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm uppercase tracking-[0.2em] text-cynos-red">FAQ</p>
                    <p className="mt-2 text-lg text-white">{item.question}</p>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-white transition ${activeIndex === index ? "rotate-180" : ""}`} />
                </div>
                {activeIndex === index && <p className="mt-4 text-sm text-zinc-300">{item.answer}</p>}
              </button>
            ))}
          </div>
          <div className="mt-8 rounded-3xl border border-white/10 bg-black/40 p-6 text-sm text-zinc-300">
            <p className="font-semibold text-white">Need help?</p>
            <p className="mt-2">Contact our support team for assisted returns, exchange requests, or delivery questions.</p>
          </div>
        </motion.section>
      </motion.div>
    </main>
  );
}
